import { ClickAwayListener, Popper, PopperPlacementType, Theme } from '@mui/material';
import cn from 'classnames';
import { differenceInDays, format, isBefore, isSameDay } from 'date-fns';
import { noop } from 'lodash';
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DateFormatsEnum } from '../../../../constants/date';
import useModal from '../../../../hooks/useModal';
import { IDatePickerOutput, IDatePickerRef, UISizeEnum } from '../../../../types/shell';
import { IFiltersRef } from '../../../../types/tables';
import { getUTCDate } from '../../../../utils/date';
import { ActionButton, ControlButton } from '../../../index';
import DatePicker from '../DatePicker/DatePicker';

import styles from './DateRangePicker.module.scss';

interface IProps {
  value: IDatePickerOutput | null;
  maxDate?: Date;
  wide?: boolean;
  openUpward?: boolean;
  alignToTriggerStart?: boolean;
  onReset?: () => void;
  onSelect?: (value: IDatePickerOutput) => void;
  labelClassName?: string;
  muiArrow?: boolean;
  aboveDialog?: boolean;
  disablePortal?: boolean;
  filtersRef?: ForwardedRef<IFiltersRef>;
}

const UPWARD_POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 10] } }];

interface IControlProps {
  close: () => void;
}

const DateRangePicker = forwardRef<IDatePickerRef, IProps>(
  (
    {
      value,
      maxDate,
      onReset,
      onSelect,
      wide,
      openUpward,
      alignToTriggerStart = false,
      labelClassName,
      muiArrow = false,
      aboveDialog = false,
      disablePortal = false,
    }: IProps,
    ref,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isWideOpened, setIsWideOpened] = useState(false);

    const { from, to } = useMemo(() => value || ({} as IDatePickerOutput), [value]);
    const maximumDate = useMemo(
      () => (maxDate ? getUTCDate(maxDate.toISOString()) : getUTCDate(new Date().toISOString())),
      [maxDate],
    );

    const [startDate, setStartDate] = useState<Date | null>(from ? getUTCDate(from) : null);
    const [endDate, setEndDate] = useState<Date | null>(to ? getUTCDate(to) : null);

    const startDateRef = useRef(startDate);
    const endDateRef = useRef(endDate);

    startDateRef.current = startDate;
    endDateRef.current = endDate;

    const {
      Modal: MobileModal,
      openModal: openMobileModal,
      closeModal: closeMobileModal,
      isOpen: isMobileModalOpened,
    } = useModal({ resetStyles: true });

    const handleSelect = useCallback(
      ({ close }: IControlProps) =>
        () => {
          const currentStartDate = startDateRef.current;
          const currentEndDate = endDateRef.current;

          const newFrom = currentStartDate ? format(currentStartDate, DateFormatsEnum.Start) : null;
          const newTo = (() => {
            if (currentEndDate) {
              return format(currentEndDate, DateFormatsEnum.End);
            }
            if (currentStartDate) {
              return format(currentStartDate, DateFormatsEnum.End);
            }
            return null;
          })();

          onSelect?.({
            from: newFrom,
            to: newTo,
          });

          close();
        },
      [onSelect],
    );

    const handleReset = useCallback(() => {
      setStartDate(null);
      setEndDate(null);
      onReset?.();
    }, [onReset]);

    useImperativeHandle(
      ref,
      () => ({
        resetDate: handleReset,
        click: () => {
          buttonRef.current?.click();
        },
        resetFilters: noop,
        resetSearch: noop,
      }),
      [handleReset],
    );

    useEffect(() => {
      if (startDate && endDate && isBefore(endDate, startDate)) {
        setEndDate(null);
      }
    }, [startDate, endDate]);

    const handleOpen = useCallback(() => {
      if (from) {
        setStartDate(getUTCDate(from));
      }
      if (to) {
        setEndDate(getUTCDate(to));
      }
    }, [from, to]);

    const handleClose = useCallback(() => {
      const currentStartDate = startDateRef.current;
      const currentEndDate = endDateRef.current;
      if (!currentStartDate && !currentEndDate) {
        onSelect?.({ from: null, to: null });
      }
    }, [onSelect]);

    const closeWide = useCallback(() => {
      setIsWideOpened(false);
      handleClose();
    }, [handleClose]);

    const handleWideToggle = useCallback(() => {
      if (isWideOpened) {
        closeWide();
        return;
      }
      setIsWideOpened(true);
      handleOpen();
    }, [isWideOpened, closeWide, handleOpen]);

    const handleClickAway = useCallback(
      (event: MouseEvent | TouchEvent) => {
        if (buttonRef.current?.contains(event.target as Node)) {
          return;
        }
        closeWide();
      },
      [closeWide],
    );

    const handleWideKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key !== 'Escape' || !isWideOpened) {
          return;
        }
        event.stopPropagation();
        event.preventDefault();
        closeWide();
        buttonRef.current?.focus();
      },
      [isWideOpened, closeWide],
    );

    const isStartDate = useCallback(
      (d: Date) => (startDate && isSameDay(startDate, d) ? styles.start : null),
      [startDate],
    );

    const handleModalOpen = useCallback(() => {
      handleOpen();
      openMobileModal();
    }, [handleOpen, openMobileModal]);

    useEffect(() => {
      if (wide && isMobileModalOpened) {
        closeMobileModal();
      }
    }, [wide, closeMobileModal, isMobileModalOpened]);

    useEffect(() => {
      if (!wide) {
        setIsWideOpened(false);
      }
    }, [wide]);

    const dayCount = useMemo(() => {
      if (startDate && endDate) {
        return differenceInDays(endDate, startDate) + 1;
      }
      if (startDate) {
        return 1;
      }
      return 0;
    }, [startDate, endDate]);

    const label = useMemo(() => {
      if (from || to) {
        const fromLabel = from ? format(getUTCDate(from), 'dd MMM yyyy') : 'Select';
        const toLabel = to ? format(getUTCDate(to), 'dd MMM yyyy') : 'Select';

        return `${fromLabel} - ${toLabel}`;
      }
      return 'Select date range';
    }, [from, to]);

    const renderControls = useCallback(
      ({ close }: IControlProps) => (
        <div className={styles.controls}>
          <div className={styles.rangeCount}>
            {dayCount} day{dayCount === 1 ? '' : 's'}
          </div>
          <ActionButton
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            outlined
            onClick={handleReset}
            dataAction="date_picker_reset"
          >
            Reset
          </ActionButton>
          <ActionButton
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            onClick={handleSelect({ close })}
            dataAction="date_picker_select"
          >
            Select
          </ActionButton>
        </div>
      ),
      [handleReset, handleSelect, dayCount],
    );

    const shrinkLayout = useMemo(
      () => (
        <MobileModal
          className={cn({ [styles.aboveDialogModal]: aboveDialog })}
          overlayClassName={cn({ [styles.aboveDialogOverlay]: aboveDialog })}
        >
          {() => (
            <div className={styles.picker}>
              <div className={cn(styles.calendarsPanel)}>
                <DatePicker
                  openToDate={startDate || undefined}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={([start, end]: [Date, Date]) => {
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  selectsRange
                  maxDate={maximumDate}
                  inline
                  disabledKeyboardNavigation
                  dayClassName={isStartDate}
                />
              </div>
              {renderControls({ close: closeMobileModal })}
            </div>
          )}
        </MobileModal>
      ),
      [MobileModal, startDate, endDate, renderControls, closeMobileModal, isStartDate, maxDate, aboveDialog],
    );

    const wideLayout = useMemo(() => {
      return (
        <>
          <DatePicker
            selectsStart
            openToDate={startDate || undefined}
            startDate={startDate}
            endDate={endDate}
            onChange={(newStartDate) => setStartDate(Array.isArray(newStartDate) ? newStartDate[0] : newStartDate)}
            maxDate={maximumDate}
            inline
            disabledKeyboardNavigation
            dayClassName={isStartDate}
          />
          <DatePicker
            disabledKeyboardNavigation
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            openToDate={endDate || undefined}
            onChange={(newEndDate) => setEndDate(Array.isArray(newEndDate) ? newEndDate[1] : newEndDate)}
            maxDate={maximumDate}
            minDate={Array.isArray(startDate) ? startDate[0] : startDate}
            inline
          />
        </>
      );
    }, [startDate, endDate, isStartDate, maxDate]);

    const verticalSide = openUpward ? 'top' : 'bottom';
    const popperPlacement: PopperPlacementType = alignToTriggerStart ? `${verticalSide}-start` : verticalSide;

    return wide ? (
      <span className={labelClassName} onKeyDown={handleWideKeyDown}>
        <ControlButton
          ref={buttonRef}
          label={label}
          isOpened={isWideOpened}
          onClick={handleWideToggle}
          muiArrow={muiArrow}
          dataAction="date_picker"
          ariaHasPopup="dialog"
          filterStyle
        />
        <Popper
          role="dialog"
          aria-label="Date range"
          open={isWideOpened}
          anchorEl={buttonRef.current}
          placement={popperPlacement}
          modifiers={openUpward ? UPWARD_POPPER_MODIFIERS : undefined}
          disablePortal={disablePortal}
          sx={{ zIndex: (theme: Theme) => theme.zIndex.modal }}
        >
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClickAway}>
            <div className={styles.picker} data-action="dropdown_content">
              <div className={cn(styles.calendarsPanel)}>{wideLayout}</div>
              {renderControls({ close: closeWide })}
            </div>
          </ClickAwayListener>
        </Popper>
      </span>
    ) : (
      <div className={labelClassName}>
        <ControlButton
          ref={buttonRef}
          label={label}
          isOpened={isMobileModalOpened}
          onClick={handleModalOpen}
          ariaHasPopup="dialog"
          filterStyle
        />
        {shrinkLayout}
      </div>
    );
  },
);

export default DateRangePicker;
